import { z } from "zod";
import { notificationDocumentSchema } from "@event-planner/shared";

/**
 * Admin notification compose form schema (ADR-120).
 *
 * Per ADR-083, the canonical Notification doc shape lives in
 * `packages/shared/src/schemas/notification.ts`. The form layer adds
 * UI orchestration concepts that don't exist on the doc:
 *  - `scheduleMode` — UI radio toggle that maps to `status` +
 *    `scheduledAt` at the service boundary.
 *
 * Audience targeting: the form binds `pickedAudienceIds[]` (named
 * audiences plus personal-audience IDs derived from picked emails).
 * The service either reuses a single audience or composes an anonymous
 * audience around them via writeBatch. The CF later resolves the
 * audience and freezes the recipient set into `targetedPersonIds[]`
 * (ADR-137).
 *
 * Setting `status: 'sent'` on create (scheduleMode === 'now') triggers
 * the existing `sendNotification` Cloud Function, which fans out push
 * + email delivery and creates `notificationReceipts` for the in-app
 * inbox. Setting `status: 'scheduled'` with a future `scheduledAt`
 * defers delivery to `processScheduledNotifications`.
 *
 * Email bodies are plain text in MVP — the service layer wraps the body
 * in a minimal HTML shell before writing to Firestore. Rich text is
 * deferred post-MVP.
 */
const notificationDocumentFormBase = notificationDocumentSchema.pick({
  title: true,
  body: true,
  channels: true,
  emailSubject: true,
  deepLink: true,
});

export const notificationSchema = z
  .object({
    ...notificationDocumentFormBase.shape,
    title: z
      .string()
      .min(1, "Title is required")
      .max(100, "Title must be 100 characters or less"),
    body: z
      .string()
      .min(1, "Body is required")
      .max(500, "Body must be 500 characters or less"),
    channels: z
      .array(z.enum(["push", "email"]))
      .min(1, "Select at least one delivery channel"),
    pickedAudienceIds: z
      .array(z.string())
      .min(1, "Pick at least one audience"),
    scheduleMode: z.enum(["now", "later"]),
    /** datetime-local input value, interpreted in the event's timezone. */
    scheduledAt: z.string().nullable(),
  })
  .refine(
    (data) =>
      !data.channels.includes("email") ||
      (data.emailSubject !== null && data.emailSubject.length > 0),
    {
      message: "Email subject is required when sending via email",
      path: ["emailSubject"],
    },
  )
  .refine(
    (data) =>
      data.scheduleMode !== "later" ||
      (data.scheduledAt !== null && data.scheduledAt.length > 0),
    {
      message: "Pick a date and time to schedule",
      path: ["scheduledAt"],
    },
  );

export type NotificationFormData = z.infer<typeof notificationSchema>;
